import React, { useState } from "react";
import data from "../data/data.json";
import { useLanguage } from "./LanguageContext";

/**
 * SearchBar-Komponente
 * - ermöglicht die Suche über mehrere Stand-Felder
 * - zeigt gefilterte Vorschläge an
 * - sortiert Vorschläge nach 'label' (z. B. 110-1, 110-2, 111-1 …)
 * - hebt passende Suchbegriffe visuell hervor
 */
export default function SearchBar({ onSelect }) {
  const { lang } = useLanguage(); // aktuelle Sprache (z. B. "de" oder "en")
  const [query, setQuery] = useState(""); // Suchtext
  const [suggestions, setSuggestions] = useState([]); // gefilterte Vorschläge

  /**
   * Übersetzt ein Feld basierend auf der aktuellen Sprache.
   * @param {*} field Text oder Objekt mit Sprachdefinitionen
   * @param {string} fallback Fallback-Sprache
   * @returns übersetzter String
   */
  const t = (field, fallback = "de") => {
    if (!field) return "";
    if (typeof field === "string") return field;
    return field[lang] ?? field[fallback] ?? "";
  };

  // Felder, die bei der Suche durchsucht werden
  const fields = ["id", "company", "product", "category", "description", "street", "city", "region", "contact"];

  /**
   * Zerlegt den Suchtext in einzelne Wörter.
   * @param {string} value
   * @returns Array von Suchwörtern
   */
  const getWords = (value) =>
    value
      .toLowerCase()
      .split(/[\s,;|]+/) // aufteilen bei Leerzeichen und Trennzeichen
      .filter(Boolean); // leere Einträge entfernen

  /**
   * Prüft, ob ein Stand zu einem der Suchwörter passt.
   * @param {*} stand Stand-Datensatz
   * @param {*} words Suchwörter
   * @returns boolean
   */
  const matchesAnyWord = (stand, words) =>
    words.some((word) =>
      fields.some((f) => {
        const fieldText = t(stand[f]).toLowerCase();
        return fieldText
          .split(/[\s,;|]+/)
          .some((token) => token.includes(word.toLowerCase()));
      })
    );

  /**
   * Wird aufgerufen bei Änderung im Suchfeld.
   * Filtert und setzt passende Vorschläge.
   */
  const handleChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    const words = getWords(value);

    if (words.length === 0) {
      setSuggestions([]);
      onSelect([]); // keine Treffer
      return;
    }

    const matches = data.stands.filter((stand) => matchesAnyWord(stand, words));

    setSuggestions(matches);
    onSelect(matches); // an StandPlan weiterreichen für Hervorhebung
  };

  /**
   * Wenn ein Vorschlag ausgewählt wird.
   * Setzt Textfeld auf Firma + Produkt, leert Vorschläge und gibt Einzelstand weiter.
   */
  const handleSelect = (stand) => {
    setQuery(`${t(stand.company)} – ${t(stand.product)}`);
    setSuggestions([]);
    onSelect([stand]);
  };

  /** Löscht Eingabe und Vorschläge */
  const handleClear = () => {
    setQuery("");
    setSuggestions([]);
    onSelect([]);
  };

  const words = getWords(query);

  /**
   * Hebt passende Teile eines Textes basierend auf Suchwörtern hervor.
   * @param {string} text
   * @returns JSX mit Markierungen
   */
  const highlight = (text) => {
    if (!words.length) return text;
    let result = [text];

    words.forEach((word) => {
      const regex = new RegExp(`(${word})`, "gi");
      result = result.flatMap((part, index) => {
        if (typeof part !== "string") return [part];
        const segments = [];
        let lastIndex = 0, match;
        while ((match = regex.exec(part)) !== null) {
          const start = match.index, end = start + match[0].length;
          if (start > lastIndex) segments.push(part.slice(lastIndex, start));
          segments.push(
            <mark key={`${index}-${start}`} className="bg-yellow-200">
              {part.slice(start, end)}
            </mark>
          );
          lastIndex = end;
        }
        if (lastIndex < part.length) segments.push(part.slice(lastIndex));
        return segments;
      });
    });

    return result;
  };

  /**
   * Sortiert die Vorschläge nach numerischem `label`.
   * Z. B. 110-1, 110-2, 111-1, 111-2 …
   */
  const sortedSuggestions = [...suggestions].sort((a, b) => {
    const parseLabel = (label) =>
      label
        .split("-")
        .map(Number); // "110-1" -> [110, 1]

    const la = parseLabel(a.label);
    const lb = parseLabel(b.label);

    for (let i = 0; i < Math.min(la.length, lb.length); i++) {
      if (la[i] !== lb[i]) return la[i] - lb[i];
    }
    return 0; // gleich
  });

  return (
    <div className="flex items-start w-full">

      {/* Sucheingabefeld */}
      <div className="relative w-80 shrink-0">
        <input
          type="text"
          value={query}
          onChange={handleChange}
          placeholder={
            lang === "de"
              ? "Suche nach Firma, Produkt, Kategorie…"
              : "Search company, product, category…"
          }
          className="w-full p-2 pr-10 rounded border"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
          >
            ✕
          </button>
        )}
      </div>

      {/* Vorschläge */}
      {sortedSuggestions.length > 0 && (
        <div className="ml-4 flex-1 max-h-64 overflow-y-auto border rounded bg-white p-2 grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {sortedSuggestions.map((s) => {
            const isMatch = matchesAnyWord(s, words);

            return (
              <div
                key={s.id}
                onClick={() => handleSelect(s)}
                className={`cursor-pointer border rounded p-3 transition ${
                  isMatch ? "border-green-500 bg-green-50" : "hover:bg-gray-100"
                }`}
              >
                <div className="font-semibold truncate">{highlight(t(s.label))}</div>
                <div className="font-semibold truncate">{highlight(t(s.company))}</div>
                <div className="text-sm text-gray-600 truncate">{highlight(t(s.product))}</div>
                <div className="text-xs text-gray-400">{highlight(t(s.category))}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
